import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import clsx from 'clsx'
import { LoadFilesError } from '@/assets/illustration'
import { Retry } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import {
  selectCurrentFolderDriveVisibleChildren,
  selectCurrentFolderId,
  selectIsCurrentFolderLoading,
  selectAreCurrentFolderChildrenLoaded,
  selectChildFoldersRequest,
} from '../store/selectors'
import { UserFoldersActions } from '../store/slice'
import { selectCurrentFolderDriveVisibleFiles, selectFileListRequest } from '@/user_files/files/store/selectors'
import { UserFilesActions } from '@/user_files/files/store/slice'
import { FolderListItem } from './FolderListItem'
import { FileListItem } from '@/user_files/files/components/FileListItem'
import { DriveEmptyState } from '@/user_files/drive/components/DriveEmptyState'
import { FolderResponse } from '../schemas'
import { UserFileRead } from '@/uppy-file-upload/schemas'

type DriveRow = { kind: 'folder'; data: FolderResponse } | { kind: 'file'; data: UserFileRead }

// Module-level — stable reference prevents table re-initialization on every render
const columnHelper = createColumnHelper<DriveRow>()

interface DriveSelectionProps {
  openMoveItems: (items: { fileIds?: string[]; folderIds?: string[] }) => void

  // File selection
  isFileSelected: (fileId: string) => boolean
  toggleFileSelect: (fileId: string) => void
  openDeleteForFile: (fileId: string) => void
  openRenameForFile: (fileId: string) => void

  // Folder selection
  isFolderSelected: (folderId: string) => boolean
  toggleFolderSelect: (folderId: string) => void
  openDeleteForFolder: (folderId: string) => void
  openRenameForFolder: (folderId: string) => void

  // Unified
  isAllSelected: boolean
  selectAll: () => void
}

interface FolderListProps {
  selection?: DriveSelectionProps
}

/**
 * FolderList
 *
 * Table-based list of child folders in the current folder.
 * Uses TanStack Table for column definitions and sticky header rendering.
 */
export function FolderList({ selection }: FolderListProps) {
  const dispatch = useAppDispatch()
  const currentFolderId = useAppSelector(selectCurrentFolderId)
  const folders = useAppSelector(selectCurrentFolderDriveVisibleChildren)
  const files = useAppSelector(selectCurrentFolderDriveVisibleFiles)
  const isLoadingFolders = useAppSelector(selectIsCurrentFolderLoading)
  const childrenLoaded = useAppSelector(selectAreCurrentFolderChildrenLoaded)
  const childFoldersRequest = useAppSelector((state) => selectChildFoldersRequest(state, { parentId: currentFolderId }))
  const fileListRequest = useAppSelector((state) => selectFileListRequest(state, { folderId: currentFolderId }))

  const hasItems = folders.length > 0 || files.length > 0
  const filesLoaded = fileListRequest.status === 'success'
  const loadError = childFoldersRequest.status === 'error' ? childFoldersRequest.error : null
  const showErrorState = Boolean(loadError)
  const showLoadingState = !showErrorState && (isLoadingFolders || !childrenLoaded || !filesLoaded)
  const shouldRenderDataRows = !showErrorState && childrenLoaded && filesLoaded && hasItems

  // Local state for overflow detection
  const [isOverflowScrollActive, setIsOverflowScrollActive] = useState(false)
  const scrollingContainerRef = useRef<HTMLDivElement>(null)

  /**
   * Detects if the scroll container overflows vertically.
   * When it does, we adjust border styling.
   */
  useLayoutEffect(() => {
    const el = scrollingContainerRef.current
    if (!el) return
    const update = () => setIsOverflowScrollActive(el.scrollHeight > el.clientHeight)

    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)

    return () => ro.disconnect()
  }, [folders.length, files.length, showLoadingState])

  const rows = useMemo<DriveRow[]>(
    () => [...folders.map((data) => ({ kind: 'folder' as const, data })), ...files.map((data) => ({ kind: 'file' as const, data }))],
    [folders, files]
  )

  const disabled = !selection
  const handleRetry = () => {
    if (!currentFolderId) return
    dispatch(UserFoldersActions.fetchFolderChildren({ folderId: currentFolderId, force: true }))
    dispatch(UserFilesActions.fetchFilesForFolder({ folderId: currentFolderId }))
  }

  const { isAllSelected, selectAll } = selection ?? {}

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: () => (
          <input
            type="checkbox"
            checked={isAllSelected ?? false}
            onChange={selectAll}
            disabled={disabled}
            className="size-5 rounded border-brand-neutral-300 text-brand-500 focus:ring-brand-500 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            aria-label="Select all items"
          />
        ),
        meta: { headerClassName: 'w-12 pl-4 pr-3 md:pl-2' },
      }),
      columnHelper.display({
        id: 'name',
        header: 'Name',
        meta: { headerClassName: 'pl-0 pr-6' },
      }),
      columnHelper.display({
        id: 'modified',
        header: 'Last Modified',
        meta: { headerClassName: 'px-6 hidden w-[22%] min-w-0 overflow-hidden text-ellipsis whitespace-nowrap [@container_(min-width:_640px)]:table-cell' },
      }),
      columnHelper.display({
        id: 'size',
        header: 'Size',
        meta: { headerClassName: 'px-6 hidden w-[12%] min-w-0 overflow-hidden text-ellipsis whitespace-nowrap [@container_(min-width:_768px)]:table-cell' },
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        meta: { headerClassName: 'w-12 px-2' },
      }),
      columnHelper.display({
        id: 'overflow',
        header: '',
        meta: { headerClassName: 'w-10 pl-0 pr-2 md:pr-0' },
      }),
    ],
    [isAllSelected, selectAll, disabled]
  )

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => `${row.kind}-${row.data.id}`,
  })

  return (
    <div className="flex min-w-0 flex-col max-h-full mt-2 overflow-hidden [container-type:inline-size]">
      <div
        ref={scrollingContainerRef}
        className={clsx('relative overflow-y-auto', isOverflowScrollActive && 'flex-1 min-h-0', !shouldRenderDataRows && 'min-h-36')}
      >
        <table className="w-full table-fixed border-collapse">
          <caption className="sr-only">List of folders and files</caption>
          <thead className="bg-brand-neutral-50 sticky top-0 z-[1] text-sm text-brand-neutral-800 border-b [&>tr>th]:py-3 [&>tr>th]:text-left [&>tr>th]:font-medium">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className={header.column.columnDef.meta?.headerClassName}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className={clsx('text-sm', shouldRenderDataRows && 'border-b border-brand-neutral-200')}>
            {/* Loading state */}
            {showLoadingState && (
              <tr>
                <td colSpan={columns.length}>
                  <div className="flex items-center justify-center h-36">
                    <CircularProgressContinuousSized size={24} thickness={5} />
                  </div>
                </td>
              </tr>
            )}

            {/* Error state */}
            {showErrorState && (
              <tr>
                <td colSpan={columns.length}>
                  <div className="flex h-36 flex-col items-center justify-center gap-4 px-6">
                    <LoadFilesError className="h-10 w-auto shrink-0 text-true-gray-300" aria-hidden />
                    <div className="flex max-w-[292px] flex-col items-center gap-0.5">
                      <p className="text-center text-sm font-medium leading-5 tracking-tight text-red-800">{loadError ?? 'Unable to load this folder.'}</p>
                      <Button
                        type="button"
                        variant="plain"
                        color="secondary"
                        size="sm"
                        trailingIcon={<Retry className="h-4 w-4 shrink-0" aria-hidden />}
                        onClick={handleRetry}
                      >
                        Retry
                      </Button>
                    </div>
                  </div>
                </td>
              </tr>
            )}

            {/* Empty state */}
            {!showLoadingState && !showErrorState && childrenLoaded && !hasItems && (
              <tr>
                <td colSpan={columns.length}>
                  <DriveEmptyState />
                </td>
              </tr>
            )}

            {/* Folder and file rows */}
            {shouldRenderDataRows &&
              table.getRowModel().rows.map((row) => {
                const item = row.original
                if (item.kind === 'folder') {
                  return (
                    <FolderListItem
                      key={row.id}
                      folder={item.data}
                      disabled={disabled}
                      selected={selection?.isFolderSelected(item.data.id)}
                      onToggleSelect={() => selection?.toggleFolderSelect(item.data.id)}
                      onRename={() => selection?.openRenameForFolder(item.data.id)}
                      onDelete={() => selection?.openDeleteForFolder(item.data.id)}
                      onMove={() => selection?.openMoveItems({ folderIds: [item.data.id] })}
                    />
                  )
                }
                return (
                  <FileListItem
                    key={row.id}
                    file={item.data}
                    disabled={disabled}
                    selected={selection?.isFileSelected(item.data.id) ?? false}
                    onToggleSelect={() => selection?.toggleFileSelect(item.data.id)}
                    onRename={() => selection?.openRenameForFile(item.data.id)}
                    onDelete={() => selection?.openDeleteForFile(item.data.id)}
                    onMove={() => selection?.openMoveItems({ fileIds: [item.data.id] })}
                  />
                )
              })}
          </tbody>
        </table>
        <div className="h-32" />
      </div>
    </div>
  )
}
